import {
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';

import * as Highcharts from 'highcharts';
import patternFill from 'highcharts/modules/pattern-fill';
patternFill(Highcharts);

import { get, merge, uniqueId, isEmpty, debounce } from 'lodash';
import { HighchartsColorCodes } from '../../../services/highcharts/types';
import { TimeoutWrapperService } from '../../../providers/timeout.wrapper/timeout.wrapper.service';
import { IBulletChartConfig, IBulletChartData, IChartOptions } from './bullet-chart.model';
import { UserService } from '../../../providers/user/user.service';

@Component({
  selector: 'viavi-bullet-chart',
  templateUrl: './bullet-chart.component.html',
  styleUrls: ['./bullet-chart.component.scss']
})
export class BulletChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() datasource: IBulletChartData;
  @Input() options?: IChartOptions;
  idData: string;
  chart: Highcharts.Chart;
  target = 90;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  boundedWindowResizeListener: any;

  constructor(
    private readonly timeout: TimeoutWrapperService,
    private readonly userService: UserService
  ) {
    this.idData = uniqueId('bullet-chart_');
    this.boundedWindowResizeListener = debounce(() => {
      this.onResizeEvent();
    }, 400);
  }

  @HostListener('window:resize')
  // eslint-disable-next-line
  onResize(event): void {
    this.boundedWindowResizeListener(event);
  }

  ngOnInit(): void {
    this._createChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.datasource) {
      if (changes.datasource.currentValue) {
        this.onUpdate();
      }
    }
  }

  ngOnDestroy() {
    if (!isEmpty(this.chart)) {
      this.chart.destroy();
    }

    if (this.timeout) {
      // we can do that since timeout service is privately provided to this widget
      this.timeout.onDestroy();
    }
  }

  onResizeEvent(): void {
    if (!isEmpty(this.chart)) {
      this.chart.destroy();
    }
    this.timeout.add(() => this.draw(), 0);
  }

  onUpdate(): void {
    if (this.chart) {
      this._updateChart();
    } else {
      this._createChart();
    }
  }

  private _createChart(): void {
    if (!isEmpty(this.chart)) {
      this.chart.destroy();
    }

    this.timeout.add(() => this.draw(), 0);
  }

  private _updateChart(): void {
    this.chart.update(this._getChartOptions());
  }

  private draw(): void {
    // if (this.datasource?.data && this.datasource.data.length > 0) {
    this.chart = Highcharts.chart(this.idData, this._getChartOptions());
    //}
  }

  private _getChartOptions(): Highcharts.Options {
    const data = this.datasource.data;
    const title = this.datasource.title;
    const config: Highcharts.Options = {
      series: [
        {
          type: 'bullet',
          pointWidth: 20,
          pointPadding: 1.25,
          borderWidth: 0,
          color: HighchartsColorCodes.BLACK,

          targetOptions: {
            width: '200%',
            color: HighchartsColorCodes.ATLANTIS,
            height: 5,
            borderColor: HighchartsColorCodes.DARKER_GRAY,
            borderRadius: 4,
            borderWidth: 1
          },
          data: this._getChartData(data)
        }
      ],
      chart: {
        marginTop: 30,
        inverted: true,
        marginLeft: 100
      },
      lang: {
        noData: 'No data found'
      },
      legend: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      exporting: {
        enabled: false
      },
      title: {
        text: title,
        style: {
          fontSize: '14px'
        }
      },
      xAxis: {
        categories: this.getXAxisCategories(data)
      },
      yAxis: {
        title: null,
        gridLineWidth: 0
      },
      tooltip: {
        formatter: function () {
          if (data[this.point.index].customTooltip) {
            return data[this.point.index].customTooltip;
          }
          const hoverToltipData = data[this.point.index].additionalTooltip;
          const tooltipContent = [];
          if (data[this.point.index]?.timeStamp) {
            tooltipContent.push('<span style="font-size: 10px">');
            tooltipContent.push(data[this.point.index].timeStamp);
            tooltipContent.push('</span><br/>');
          }

          tooltipContent.push('<span style="font-size: 12px;">');
          tooltipContent.push(this.point.category + ': ' + this.point.y + 'Mbps');
          tooltipContent.push('</span><br/>');
          if (hoverToltipData) {
            hoverToltipData.map(val => {
              tooltipContent.push('<span  style="font-size: 12px;">');
              if (val.color) {
                tooltipContent.push('<span style="color: ' + val.color + '">');
                tooltipContent.push('● ');
                tooltipContent.push('</span>');
              }
              tooltipContent.push(val.label + ':  ' + val.value);
              tooltipContent.push('</span><br/>');
            });
          }
          return tooltipContent.join('');
        }
      }
    };
    return merge({}, config, get(this.options, 'override'));
  }

  private getXAxisCategories(data: IBulletChartConfig[]) {
    if (Array.isArray(data)) {
      return data.map(item => item.name);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _getChartData(data: IBulletChartConfig[]): any {
    if (!data || data.length === 0) {
      return [];
    }
    const chartData = [];
    data.forEach((item, index) => {
      const dataItem = {
        y: item?.value,
        target: item.target,
        color: this.assignColor(
          HighchartsColorCodes[Object.keys(HighchartsColorCodes)[index]],
          item
        ),
        targetOptions: {
          width: item?.value ? '200%' : '0%'
        },
        className: item.color === 'pattern' ? 'pattern' : ''
      };
      chartData.push(dataItem);
    });
    return chartData;
  }

  //eslint-disable-next-line
  private assignColor(color, data): any {
    if (data.color === 'pattern') {
      return this._getPatternColor();
    } else {
      return data.color ?? color;
    }
  }

  //eslint-disable-next-line
  private _getPatternColor(): any {
    return {
      pattern: {
        color: this.userService.getThemeFromStorage() === 'dark' ? '#FFFFFF' : '#434348',
        path: {
          d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
          strokeWidth: 3
        },
        width: 10,
        height: 10,
        opacity: 0.7
      }
    };
  }
}
